import { memo, useContext, useState, useDeferredValue, Suspense } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { DocumentNode, OperationVariables, SkipToken, skipToken } from '@apollo/client';
import { Button } from 'src/modules/common/components/Button/Button';
import { ResetIcon } from 'src/modules/common/icons/ResetIcon';
import { CrossIcon } from 'src/modules/common/icons/CrossIcon';
import { RuntimeContext } from 'src/runtimeContext';
import { I18n } from 'src/modules/common/components/I18n/I18n';
import { Sidebar } from 'src/modules/common/components/Sidebar/Sidebar';
import { Filter, FilterConfig } from 'src/utils/composeFilter';
import { CheckboxHandler } from 'src/modules/common/types/CheckboxHandler';
import { SkeletonFilter } from 'src/modules/common/skeletons/SkeletonFilter/SkeletonFilter';
import { FilterSidebarActions } from 'src/common/FilterSidebarActions/FilterSidebarActions';
import clsx from 'clsx';
import { FilterItemQuery } from 'src/common/FilterItemQuery/FilterItemQuery';
import { FilterItemWrapper } from 'src/common/FilterItemWrapper/FilterItemWrapper';
import { FiltersQuery } from 'src/common/FiltersQuery/FiltersQuery';
import { getFilterValuesAsString } from 'src/common/Breadcrumb/getFilterValuesAsString';

type Props<Variables> = {
    filter: Filter;
    filterConfigPrimary?: FilterConfig;
    filterConfigSecondary: FilterConfig;
    queryPrimary?: DocumentNode;
    genericFilterQuery: DocumentNode;
    variables: SkipToken | { variables: Variables };
    checkboxHandler: CheckboxHandler;
    handleReset: () => void;
    label: string;
    selectedFlowRange?: string;
    onFlowRangeSelected?: (name: string) => void;
    prepareLabel?: (value: string) => string;
}

export const FilterSidebarComponent = memo(<Variables extends OperationVariables, >({
    filter,
    filterConfigPrimary,
    filterConfigSecondary,
    queryPrimary,
    genericFilterQuery,
    variables,
    checkboxHandler,
    handleReset,
    label,
    selectedFlowRange,
    onFlowRangeSelected,
    prepareLabel,
}: Props<Variables>) => {
    const { isMobile } = useContext(RuntimeContext);
    const { userSettings, rootElement, getComparator } = useContext(ConfigContext);

    const [focusedFilterName, setFocusedFilterName] = useState<string | null>(null);
    const [isFilterSidebarOpened, setFilterSidebarOpened] = useState(false);

    const checkboxHandlerWrapped: CheckboxHandler = (data: Parameters<CheckboxHandler>[0]) => {
        setFocusedFilterName(null);
        return checkboxHandler(data);
    };

    const hasAdditionalFilter = getFilterValuesAsString(
        [...(filterConfigPrimary || []), ...filterConfigSecondary],
        filter,
    ).length > 0;

    const deferredVariables = useDeferredValue(variables);

    const isStale = deferredVariables !== variables;

    const filterQueries = (
        <>
            {filterConfigPrimary && queryPrimary && (
                <Suspense
                    fallback={<SkeletonFilter mobile={isMobile} fullHeight={true}/>}
                >
                    <FiltersQuery
                        filterConfig={filterConfigPrimary}
                        filter={filter}
                        query={queryPrimary}
                        variables={deferredVariables}
                        getComparator={getComparator}
                        checkboxHandler={checkboxHandlerWrapped}
                        focusedFilterName={focusedFilterName}
                        setFocusedFilterName={setFocusedFilterName}
                    />
                </Suspense>
            )}

            {filterConfigSecondary
                .map(([name, attributeId]) => {
                    const composedVariables = deferredVariables !== skipToken ? {
                        variables: {
                            ...deferredVariables.variables,
                            id: attributeId,
                        },
                    } : skipToken;

                    return (
                        <Suspense
                            key={`${userSettings.currentRegion}-${name}-${attributeId}`}
                            fallback={<SkeletonFilter mobile={isMobile}/>}
                        >
                            <FilterItemWrapper>
                                <FilterItemQuery
                                    name={name}
                                    filter={filter}
                                    query={genericFilterQuery}
                                    variables={composedVariables}
                                    comparator={getComparator(attributeId)}
                                    checkboxHandler={checkboxHandlerWrapped}
                                    focusedFilterName={focusedFilterName}
                                    setFocusedFilterName={setFocusedFilterName}
                                    selectedFlowRange={selectedFlowRange}
                                    onFlowRangeSelected={onFlowRangeSelected}
                                    prepareLabel={prepareLabel}
                                />
                            </FilterItemWrapper>
                        </Suspense>
                    );
                })}
        </>
    );

    return (
        <div className={clsx({
            'vss-filter-sidebar-component': true,
            'vss-filter-sidebar-component--stale': !isMobile && isStale,
        })}
        >
            {isMobile ? (
                <>
                    <FilterSidebarActions
                        useTranslate={false}
                        filter={filter}
                        config={[...(filterConfigPrimary || []), ...filterConfigSecondary]}
                        handleFilterSidebarOpen={() => setFilterSidebarOpened(true)}
                        filterCloseHandler={checkboxHandler}
                    />

                    <Sidebar
                        opened={isFilterSidebarOpened}
                        handleClose={() => setFilterSidebarOpened(false)}
                        root={rootElement}
                    >
                        <div className={clsx({
                            'vss-filter-sidebar-component': true,
                            'vss-filter-sidebar-component--stale': isStale,
                        })}
                        >
                            <div className={clsx({
                                'vss-filter-sidebar-component__header': true,
                                'vss-filter-sidebar-component__header--mobile': isMobile,
                            })}
                            >
                                <p className="vss-filter-sidebar-component__name">
                                    <I18n messageId={label}/>
                                </p>

                                <Button
                                    className="vss-filter-sidebar-component__button"
                                    size="sm"
                                    iconOnly={true}
                                    onClick={() => handleReset()}
                                    kind="ghost"
                                    disabled={!hasAdditionalFilter}
                                >
                                    <div className="vss-filter-sidebar-component__icon">
                                        <ResetIcon/>
                                    </div>
                                </Button>

                                <Button
                                    className="vss-filter-sidebar-component__button"
                                    onClick={() => setFilterSidebarOpened(false)}
                                    size="sm"
                                    iconOnly={true}
                                    kind="ghost"
                                >
                                    <div className="vss-filter-sidebar-component__icon">
                                        <CrossIcon/>
                                    </div>
                                </Button>
                            </div>

                            {filterQueries}
                        </div>
                    </Sidebar>
                </>
            ) : (
                <>
                    <div className="vss-filter-sidebar-component__header">
                        <p className="vss-filter-sidebar-component__name">
                            <I18n messageId={label}/>
                        </p>

                        <Button
                            className="vss-filter-sidebar-component__button"
                            size="sm"
                            iconOnly={true}
                            onClick={() => handleReset()}
                            kind="ghost"
                            disabled={!hasAdditionalFilter}
                        >
                            <div className="vss-filter-sidebar-component__icon">
                                <ResetIcon/>
                            </div>
                        </Button>
                    </div>

                    {filterQueries}
                </>
            )}
        </div>
    );
});
